import Validators from '../validators'
import * as defaultConst from '../../../constants/default_constants'
import Moment from 'moment'
import { extendMoment } from 'moment-range'
const moment = extendMoment(Moment)

// contract_items_cost (Titles tab "Costs")
const limitsAttrs = (store) => ({
  attrs: ['platform', 'right_type', 'expense_type', 'limit', 'due_date', 'expire_date'],
  titles: [
    store.__('platform'),
    store.__('right type'),
    store.__('type'),
    store.__('limit'),
    store.__('due date'),
    store.__('expire date')
  ],
  _sortField: 'platform',
  timeRestriction: 'costs',
  validators: {
    platform: Validators.isMandatory(store),
    right_type: Validators.isMandatory(store),
    expense_type: Validators.isMandatory(store),
    limit: Validators.isMandatory(store),
    due_date: Validators.isDate(store),
    expire_date: Validators.isDate(store)
  }
})

// User can't be able to add two rights with same right_type
// overlapping in time and territory to one contractitem.
// No matter if they are exlusive or not.
function _validateNotOverlapping (store, record, items) {
  const rightsExclusive = record.rights_exclusive_flg
  const controlTimestamp = record.control_timestamp
  const errs = []
  const errsTest = []
  const countries = store.options.get('Countries')
  const getRegions = (regions) => {
    if (!regions || !regions.length) {
      return regions
    } else if (regions === 'WW') {
      return [regions]
    }
    const splitRegions = (regions.charAt(0) === '-' ? regions.substring(1) : regions).split(',')
    return regions.charAt(0) === '-'
      ? countries.map(country => !splitRegions
        .find(region => region === country.value) && country.value)
        .filter(result => result && result.length <= 2)
      : splitRegions
  }

  record.regions && getRegions(record.regions).forEach((r) => {
    items.forEach((i) => {
      // same item
      const thisItem = (i.id && i.id === record.id) ||
        (i.control_timestamp && i.control_timestamp === controlTimestamp)

      // same period
      const isOverlapping = (cStart, cEnd, nStart, nEnd) => {
        if ((!cStart && !cEnd) || (!nStart && !nEnd)) {
          // without test if values missing
          return false
        }
        const cRange = cStart && cEnd && moment.range(cStart, cEnd)
        const nRange = nStart && nEnd && moment.range(nStart, nEnd)
        return (
          cRange.overlaps(nRange) || nRange.overlaps(cRange) ||
          moment(cStart).format('YYYY-MM-DD') === moment(nEnd).format('YYYY-MM-DD') ||
          moment(nStart).format('YYYY-MM-DD') === moment(cEnd).format('YYYY-MM-DD')
        )
      }

      // same region
      if (!thisItem && i.regions && getRegions(i.regions).indexOf(r) >= 0) {
        // same period result
        const rangeOverlapping = isOverlapping(
          (record.start_license_period || record.end_license_period) &&
          new Date(record.start_license_period || record.end_license_period),
          (record.end_license_period || record.start_license_period) &&
          new Date(record.end_license_period || record.start_license_period),
          (i.start_license_period || i.end_license_period) &&
          new Date(i.start_license_period || i.end_license_period),
          (i.end_license_period || i.start_license_period) &&
          new Date(i.end_license_period || i.start_license_period)
        )

        // platform/right type test
        const rightPlatform = record.platform && i.platform && record.platform === i.platform
        const rightType = record.right_type && i.right_type && record.right_type === i.right_type
        const rightError = store.__('"Platform/Right type" are already present at the same date in another right item')

        // same right type
        if (rangeOverlapping && rightPlatform && (rightType || (i.right_type && i.right_type.toLowerCase() === 'all') ||
          (rightType && rightType.toLowerCase() === 'all'))) {
          // same right type
          if (i.id && !errsTest.includes(i.id)) {
            errs.push(rightError + store.__(' with ') + (` ID ${i.id} / `) + store.__(' region ') + r + '. ')
            errsTest.push(i.id)
          } else if (!i.id && !errsTest.includes('new')) {
            errs.push(rightError + (' / ') + store.__(' region ') + r + '. ')
            errsTest.push('new')
          }
          // same exclusive rights
          if (rightsExclusive && i.rights_exclusive_flg && i.rights_exclusive_flg === true) {
            errs.push(store.__('"Rights exclusive" are already present at the same date in another right item with') + (i.id
              ? ` ID ${i.id} `
              : store.__(' NEW ')) + ' / ' + store.__(' region ') + r + '. ')
            errsTest.push(i.id)
          }
        }
      }
    })
  })
  return (errs.length > 0 ? errs : undefined)
}

// contract_items_right
const rightsConf = (store, getMonthsAditional, batch = false) => ({
  attrs: [
    'id',
    'start_license_period',
    'end_license_period',
    'regions',
    'canceled_date',
    'status',
    'platform',
    'right_type',
    'rights_exclusive_flg',
    'can_resale',
    'pricing_tier',
    'minimum_fee_sd',
    'minimum_fee_hd'
  ],
  titles: [
    store.__('ID'),
    store.__('license starts'),
    store.__('license ends'),
    store.__('regions'),
    store.__('canceled date'),
    store.__('status'),
    store.__('platform'),
    store.__('right type'),
    store.__('rights excl.'),
    store.__('can re-sale'),
    store.__('pricing tier'),
    store.__('minimum fee SD'),
    store.__('minimum fee HD')
  ],
  _sortField: 'right_type,start_license_period',
  _sortDir: 'ASC,ASC',
  timeRestriction: 'rights',
  initVals: (row) => {
    return { rights_exclusive_flg: true, can_resale: true }
  },
  onFieldChange: {
    start_license_period: (val, state) => {
      const months = getMonthsAditional(state.record)
      if (val && val.length >= 10 && months && months > 0) {
        state.record.set('end_license_period', moment(val)
          .add(months, 'months')
          .subtract(1, 'days')
          .format(defaultConst.DATE_FORMAT))
        state.record.set('END_license_period_info',
          store.__('"License ends" date was automatically set after ') + months + store.__(' months.'))
      }
      if ((!val || val.length <=0) && state.record.has('END_license_period_info')) {
        state.record.set('END_license_period_info', null)
      }
    }
  },
  end_license_period: (val, state) => {
    if ((!val || val.length <=0) && state.record.has('END_license_period_info')) {
      state.record.set('END_license_period_info', null)
    }
  },
  validators: {
    regions: [
      Validators.isMandatory(store),
      Validators.regionTest(store)
    ],
    canceled_date: Validators.isDate(store),
    end_license_period: Validators.isDate(store),
    start_license_period: Validators.isDate(store),
    platform: Validators.isMandatory(store),
    right_type: Validators.isMandatory(store),
    status: Validators.isMandatory(store),
    _global: (vals, items) => {
      if (vals.get('start_license_period') && vals.get('end_license_period')) {
        const validFrom = new Date(vals.get('start_license_period'))
        const validTo = new Date(vals.get('end_license_period'))
        if (validFrom > validTo) {
          return [store.__('Start date cannot be less than end date.')]
        }
      }
      return batch ? undefined : _validateNotOverlapping(store, Object.fromEntries(vals), items)
    }
  }
})

// contract_items_right - copy
const rightsCopy = (store, getMonthsAditional, batch = false) => ({
  timeRestriction: 'rights',
  validators: {
    start_license_period: [
      Validators.isValidStartEnd(store)
    ],
    end_license_period: [
      Validators.isValidStartEnd(store)
    ],
    _global: val => {
      const contractitem = val.get('contractitem')
      let errors = []

      if (contractitem && contractitem !== val.get('contractitemFetched')) {
        store.requester.call(`/contractitems/${contractitem}`, 'get').then(({ data }) => {
          val.set('contractitemFetched', data.id)
          val.set('rights', [...data.rights].filter(d => !moment(d.end_license_period).isBefore(moment())))
          val.set('selection', [...data.rights].filter(d => !moment(d.end_license_period).isBefore(moment())).map(c => c.id))
        })
      } else if (!contractitem) {
        val.delete('contractitemFetched')
        val.set('rights', null)
        val.set('selection', null)
      }

      if (val.has('rights') && val.get('rights') && val.has('selection') && val.get('selection') && val.get('selection').length > 0) {
        const selectedRights = [...val.get('rights').filter(r => (val.get('selection').includes(r.id)))]

        selectedRights.forEach(record => {
          if (!record.start_license_period || moment(record.start_license_period).isBefore(moment())) {
            record.start_license_period = moment()
          }
          if (val.has('start_license_period') && val.get('start_license_period')) {
            record.start_license_period = val.get('start_license_period')
          }
          if (val.has('end_license_period') && val.get('end_license_period')) {
            record.end_license_period = val.get('end_license_period')
          }
        })
        // validation
        selectedRights.forEach(record => {
          const errorsResult = _validateNotOverlapping(store, record, selectedRights)
          errors = errorsResult && errorsResult.length > 0 ? errors.concat(errorsResult) : errors
        })
        if (errors && errors.length > 0) {
          return errors
        }
      }
    }
  }
})

// contract_items_rights_shares
const rightShares = (store) => ({
  attrs: [
    'contractor_id',
    'contract_item_right_id',
    'share',
    'platform',
    'right_type',
    'source_of_revenues_cd'
  ],
  titles: [
    store.__('contractor'),
    store.__('item right'),
    store.__('share'),
    store.__('platform'),
    store.__('right type'),
    store.__('limited to source of revenues')
  ],
  _sortField: 'contract_item_right_id',
  initVals: (row) => ({ control_timestamp: Math.round(new Date().getTime()) }),
  validators: {
    contractor_id: Validators.isMandatory(store),
    contract_item_right_id: Validators.isMandatory(store),
    platform: Validators.isMandatory(store),
    right_type: Validators.isMandatory(store),
    share: [
      Validators.isMandatory(store),
      Validators.positiveFloat(store, true),
      Validators.maxIntValue(store, 100)
    ],
    _global: (vals, items) => {
      // Validate for same contractor in same right_type twice!
      if (items.find(i => !((vals.get('id') && i.id && vals.get('id') === i.id) ||
        (vals.get('control_timestamp') && i.control_timestamp && vals.get('control_timestamp') === i.control_timestamp)) &&
        i.contractor_id === vals.get('contractor_id') &&
        i.platform === (vals.get('platform') || null) &&
        i.right_type === (vals.get('right_type') || null) &&
        i.platform === vals.get('source_of_revenues_cd'))
      ) {
        return store.__('Only one contractor per same "Platform/Right type" is alowed.')
      }
    }
  }
})

// contract_cost (Titles tab "Right costs")
// contract_items_rights_cost
const rightsCostsConfs = (store) => ({
  attrs: [
    'platform',
    'right_type',
    'contract_item_right_id',
    'expense_type',
    'limit',
    'due_date',
    'expire_date'
  ],
  titles: [
    store.__('platform'),
    store.__('right type'),
    store.__('item right'),
    store.__('type'),
    store.__('limit'),
    store.__('due date'),
    store.__('expire date')
  ],
  _sortField: 'contract_item_right_id',
  timeRestriction: 'rights_costs',
  validators: {
    platform: Validators.isMandatory(store),
    right_type: Validators.isMandatory(store),
    contract_item_right_id: Validators.isMandatory(store),
    expense_type: Validators.isMandatory(store),
    limit: Validators.isMandatory(store),
    due_date: Validators.isDate(store),
    expire_date: Validators.isDate(store)
  }
})

export default {
  limitsAttrs,
  rightsConf,
  rightsCopy,
  rightShares,
  rightsCostsConfs,
  validateNotOverlapping: _validateNotOverlapping
}
